import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from 'components/common/modal-component';
import { ModalButton } from 'models/utils/common/modal-button-model';
import Banner, { BANNER_TYPES } from 'components/common/banner-component';
import Dropdown from 'components/common/dropdown-component';
import { FormattedMessage } from 'react-intl';
import { TRIAL_BALANCE_SCALING_OPTIONS } from 'constants/feature/statement-creation-constants';
import TextInput from 'components/common/text-input-component';
import {
  checkIfStringContainsOnlySpaces,
  isThresholdValid,
  isThresholdExceeding15Digits,
  isThresholdNegative,
} from 'constants/common/feature-common-utils';
import { performTBAutoTieoutOnUpdatedSnapshot } from 'store/actions/trial-balance/trial-balance-action';

const SET_THRESHOLD_SCALING_BLOCK = 'set-threshold-scaling-modal';
const SET_THRESHOLD_SCALING_ID_BLOCK = 'set-threshold-scaling-modal-id';

const SetThresholdScalingModal = ({
  onClose,
  onCreate,
  params,
  setShowAutoTieoutInfoBanner,
  title,
  displayThreshold = true,
  displayScaling = true,
  scalingOption,
  setScalingOption,
  thresholdOption,
  setThresholdOption,
  thresholdScaling,
}) => {
  const dispatch = useDispatch();
  const tbWorkflowStatus = useSelector(
    (state) => state?.ui.sideBySideView?.trialBalance?.tbWorkFlowStatus,
  );
  const [visitedReferenceNumber, setVisitedReferenceNumber] = useState(false);

  useEffect(() => {
    setScalingOption(thresholdScaling?.scaling);
    setThresholdOption(String(thresholdScaling?.threshold));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thresholdScaling]);

  const handleSetClick = async () => {
    if (tbWorkflowStatus?.stale) {
      setShowAutoTieoutInfoBanner(true);
      dispatch(performTBAutoTieoutOnUpdatedSnapshot(params));
      onClose();
    } else {
      try {
        await onCreate({
          threshold: Number(thresholdOption),
          scaling: scalingOption,
        });
      } catch (error) {
        // Handle error if needed
        console.error('Error setting threshold and scaling:', error);
      }
    }
  };

  const thresholdErrorText = () => {
    if (isThresholdNegative(thresholdOption)) {
      return 'trial-balance-view-set-threshold-and-scaling.modal.threshold.error.range';
    }
    if (isThresholdExceeding15Digits(thresholdOption)) {
      return 'trial-balance-view-set-threshold-and-scaling.modal.threshold.error.15digit';
    }
    if (!isThresholdValid(thresholdOption)) {
      return 'trial-balance-view-set-threshold-and-scaling.modal.threshold.error.range';
    }
  };

  const isFormValid = () => {
    return isThresholdValid(thresholdOption) && scalingOption !== null;
  };

  const disabled = !isFormValid();

  return (
    <Modal
      id={SET_THRESHOLD_SCALING_ID_BLOCK}
      title={title}
      onClose={onClose}
      primaryModalButton={
        new ModalButton({
          text: 'trial-balance-view-set-threshold-and-scaling.modal.create',
          onClick: handleSetClick,
          disabled,
        })
      }
    >
      <div className={`${SET_THRESHOLD_SCALING_BLOCK}__banner`}>
        <Banner
          id={`${SET_THRESHOLD_SCALING_BLOCK}-info-banner`}
          type={BANNER_TYPES.GUIDANCE}
          bannerCopy={
            displayThreshold && !displayScaling
              ? 'trial-balance-view-set-threshold-and-scaling.threshold.modal.description'
              : 'trial-balance-view-set-threshold-and-scaling.modal.description'
          }
          isInternationalized={true}
          isInteractive={false}
          width={'100%'}
        />
      </div>
      {displayThreshold && (
        <>
          <p>
            <FormattedMessage id="trial-balance-view-set-threshold-and-scaling.modal.threshold.description" />
          </p>
          <div className={`${SET_THRESHOLD_SCALING_BLOCK}__reference-number`}>
            <TextInput
              id={`${SET_THRESHOLD_SCALING_ID_BLOCK}-engagement-name`}
              label={
                'trial-balance-view-set-threshold-and-scaling.modal.threshold'
              }
              placeholder={
                'trial-balance-view-set-threshold-and-scaling.modal.threshold'
              }
              onBlur={() => setVisitedReferenceNumber(true)}
              width={'55%'}
              onChange={setThresholdOption}
              isValid={
                isThresholdValid(thresholdOption) ||
                !visitedReferenceNumber ||
                checkIfStringContainsOnlySpaces(thresholdOption)
              }
              errorText={thresholdErrorText()}
              type={'text'}
              value={thresholdOption}
            />
          </div>{' '}
        </>
      )}
      {displayScaling && (
        <>
          <p>
            <FormattedMessage id="trial-balance-view-set-threshold-and-scaling.modal.scaling.description" />
          </p>
          <div className={`${SET_THRESHOLD_SCALING_BLOCK}__reference-number`}>
            <Dropdown
              id={`${SET_THRESHOLD_SCALING_ID_BLOCK}-scaling`}
              label={
                'trial-balance-view-set-threshold-and-scaling.modal.scaling'
              }
              placeholder={
                'trial-balance-view-set-threshold-and-scaling.modal.scaling'
              }
              width={'55%'}
              onSelectOption={setScalingOption}
              isValid={true}
              options={TRIAL_BALANCE_SCALING_OPTIONS}
              value={scalingOption}
            />
          </div>
        </>
      )}
    </Modal>
  );
};

SetThresholdScalingModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  thresholdScaling: PropTypes.object,
  editButtonClicked: PropTypes.bool.isRequired,
  title: PropTypes.string,
  displayThreshold: PropTypes.bool,
  displayScaling: PropTypes.bool,
  params: PropTypes.object,
  setShowAutoTieoutInfoBanner: PropTypes.func,
};

export default SetThresholdScalingModal;
